import React, { useEffect, useRef, useState } from "react";
import { motion, animate, useMotionValue, useMotionTemplate, useReducedMotion, useInView as useFramerInView } from "framer-motion";
import { useParallaxY } from "../../hooks/useParallaxY";

const textParallaxAmount = -15;

const Hero: React.FC = () => {
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    let rafId: number | null = null;
    const handleMouseMove = (e: MouseEvent) => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setMousePos({
          x: (e.clientX / window.innerWidth - 0.5) * 2,
          y: (e.clientY / window.innerHeight - 0.5) * 2,
        });
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const heroSectionRef = useRef<HTMLElement>(null);
  const heroBlobY = useParallaxY(heroSectionRef, [-60, 60]);

  // Frost overlay "creeps in" from the edges toward the center on load: the
  // radial mask's transparent inner radius starts covering the whole box
  // (nothing visible) and shrinks down to the image's own natural hole size.
  const frostReveal = useMotionValue(100);
  const frostMask = useMotionTemplate`radial-gradient(farthest-corner at 50% 50%, transparent ${frostReveal}%, black calc(${frostReveal}% + 22%))`;
  const prefersReducedMotionForFrost = useReducedMotion();
  const heroInView = useFramerInView(heroSectionRef, { once: false, amount: 0.4 });
  useEffect(() => {
    if (prefersReducedMotionForFrost) {
      frostReveal.set(30);
      return;
    }
    if (!heroInView) {
      frostReveal.set(100);
      return;
    }
    frostReveal.set(100);
    const controls = animate(frostReveal, 30, { duration: 3.5, delay: 0.4, ease: "easeOut" });
    return () => controls.stop();
  }, [heroInView, prefersReducedMotionForFrost]);

  return (
    <section
      ref={heroSectionRef}
      className="relative min-h-screen w-full flex items-center justify-center bg-white dark:bg-[#121212]"
    >
      <div className="relative w-full bg-white dark:bg-[#1E1E1E] pt-[100px] px-4 md:px-12 pb-10 overflow-hidden flex flex-col items-center justify-center">

        {/* Background & Grid */}
        <div className="absolute inset-0 z-0 bg-white dark:bg-[#1E1E1E]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] bg-[size:40px_60px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
          <motion.div
            className="absolute top-0 bottom-0 right-[-10%] w-[60%] bg-[#172b81]/10 dark:bg-[#172b81]/20 blur-[120px] opacity-70 rounded-full"
            style={{ y: heroBlobY }}
          />
        </div>

        {/* Edge fade */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 sm:w-24 md:w-40 bg-gradient-to-r from-white dark:from-[#1E1E1E] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 sm:w-24 md:w-40 bg-gradient-to-l from-white dark:from-[#1E1E1E] to-transparent" />

        {/* Frost overlay */}
        <motion.div
          className="absolute inset-0 z-[5] pointer-events-none overflow-hidden dark:hidden"
          style={{ WebkitMaskImage: frostMask, maskImage: frostMask }}
        >
          <img
            src="/images/overlay.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-[#c3d6e6]/30" />
        </motion.div>

        <motion.div
          className="relative z-10 text-center max-w-6xl px-2"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.3 } },
          }}
          style={{ x: mousePos.x * textParallaxAmount, y: mousePos.y * textParallaxAmount }}
        >
          <h1 className="text-[2.2rem] sm:text-[2.5rem] md:text-[3.5rem] lg:text-[6rem] font-black leading-[0.9] tracking-tighter text-slate-900 dark:text-white">
            <div className="mb-2 flex flex-wrap justify-center gap-x-4">
              <motion.span
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.035 } } }}
                style={{ display: 'inline-block' }}
              >
                {"Revolutionizing".split("").map((char, i) => (
                  <motion.span
                    key={i}
                    variants={{ hidden: { y: -28 }, visible: { y: 0 } }}
                    transition={{ type: "spring", damping: 14, stiffness: 250 }}
                    style={{ display: 'inline-block' }}
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }} className="bg-gradient-to-r from-[#172b81] to-[#204988] bg-clip-text text-transparent">Cold</motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }}>Storage,</motion.span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4">
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }}>One</motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }} className="bg-gradient-to-r from-[#e1ae48] to-[#c18e28] bg-clip-text text-transparent">Freezer</motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }} className="font-light italic text-slate-300 dark:text-slate-500">at</motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }} className="font-light italic text-slate-300 dark:text-slate-500">a</motion.span>
              <motion.span variants={{ hidden: { y: 20 }, visible: { y: 0 } }}>Time</motion.span>
            </div>
          </h1>
          <p className="mt-8 text-[10px] uppercase font-bold tracking-[0.6em] text-slate-400">Engineering Excellence since 1996</p>
        </motion.div>

        {/* Freezer Image Container */}
        <motion.div
          className="relative w-full max-w-[600px] md:max-w-[1000px] mt-10 z-10"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
        >
          <div className="relative w-full h-auto max-h-[55vh] mx-auto">
            <img
              src="/images/libherrfreezer3.webp"
              alt="Hero Freezer"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-contain [mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]"
            />
          </div>
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-80 bg-gradient-to-t from-white dark:from-[#121212] to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
